import React from 'react';

interface TechnologyItem {
    tech: string;
    metric: string;
    description: string;
}

interface TechnologyCategory {
    name: string;
    divisions: TechnologyItem[];
}

interface TechnologyAlignmentTabProps {
    keyTechnology?: TechnologyCategory[];
    strategicVendors?: { vendor: string; status: string; }[];
}

const TechnologyAlignmentTab: React.FC<TechnologyAlignmentTabProps> = ({
    keyTechnology = [],
    strategicVendors = []
}) => {
    const getStatusStyles = (metric: string) => {
        const normalized = metric.toLowerCase();

        if (normalized.includes('production')) {
            return {
                badge: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
                dot: 'bg-emerald-500',
                panel: 'from-emerald-50 to-white',
            };
        }

        if (normalized.includes('deploying')) {
            return {
                badge: 'bg-amber-100 text-amber-800 ring-amber-200',
                dot: 'bg-amber-500',
                panel: 'from-amber-50 to-white',
            };
        }

        if (normalized.includes('optimizing')) {
            return {
                badge: 'bg-orange-100 text-orange-800 ring-orange-200',
                dot: 'bg-orange-500',
                panel: 'from-orange-50 to-white',
            };
        }

        if (normalized === 'no usage') {
            return {
                badge: 'bg-rose-100 text-rose-800 ring-rose-200',
                dot: 'bg-rose-500',
                panel: 'from-rose-50 to-white',
            };
        }

        return {
            badge: 'bg-slate-100 text-slate-700 ring-slate-200',
            dot: 'bg-slate-400',
            panel: 'from-slate-50 to-white',
        };
    };

    const activeVendors = strategicVendors.filter((vendorItem) => vendorItem.status === 'Yes');

    return (
        <div className="px-4">
            <div className="mb-6">
                <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Key Technology</h1>

            </div>

            <div className="space-y-6">
                <section className="rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,42,79,0.08)]">
                    <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-indigo-50 px-5 py-4">
                        <h2 className="text-lg font-semibold text-[#0F2A4F]">Strategic Vendors</h2>
                        <p className="mt-1 text-sm text-slate-500">
                            Preferred partner ecosystem currently aligned to the customer environment.
                        </p>
                    </div>

                    <div className="p-4">
                        {activeVendors.length > 0 ? (
                            <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
                                {activeVendors.map((vendorItem, index) => (
                                    <div
                                        key={index}
                                        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5"
                                    >
                                        <span className="h-2 w-2 shrink-0 rounded-full bg-[#0F71C0]" />
                                        <span className="text-xs font-medium text-slate-700">
                                            {vendorItem.vendor}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                                No strategic vendors found.
                            </div>
                        )}
                    </div>
                </section>

                <div className="grid grid-cols-1 gap-5 2xl:grid-cols-2">
                    {keyTechnology.length > 0 ? (
                        keyTechnology.map((category, categoryIndex) => (
                            <section
                                key={categoryIndex}
                                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,42,79,0.08)]"
                            >
                                <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-sky-50 px-5 py-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <h2 className="text-lg font-semibold text-[#0F2A4F]">{category.name}</h2>
                                            <p className="mt-1 text-sm text-slate-500">
                                                {category.divisions.length} {category.divisions.length === 1 ? 'technology' : 'technologies'} tracked in this domain
                                            </p>
                                        </div>
                                        <div className="rounded-full bg-[#0F2A4F]/6 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#0F2A4F]">
                                            Domain
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-2">
                                    {category.divisions.map((tech, techIndex) => {
                                        const statusStyles = getStatusStyles(tech.metric);

                                        return (
                                            <article
                                                key={`${categoryIndex}-${techIndex}`}
                                                className={`rounded-xl border border-slate-200 bg-gradient-to-br ${statusStyles.panel} p-4`}
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusStyles.dot}`} />
                                                            <h3 className="truncate text-base font-semibold text-slate-900">{tech.tech}</h3>
                                                        </div>
                                                    </div>
                                                    <span className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusStyles.badge}`}>
                                                        {tech.metric || 'Unknown'}
                                                    </span>
                                                </div>

                                                <div className="mt-3 rounded-lg bg-white/80 px-3 py-3 text-sm leading-6 text-slate-600 ring-1 ring-slate-200/80">
                                                    {tech.description || 'No notes provided for this technology yet.'}
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            </section>
                        ))
                    ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500 2xl:col-span-2">
                            No technology alignment data present.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TechnologyAlignmentTab;
